import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
} from '@mui/material';
import { useNotify, useRefresh, useCreate, useRecordContext } from 'react-admin';
import { ProjectContactStage } from '../../types/ProjectContactStage.ts';
import { ProjectAutocomplete } from '../../components/ProjectAutocomplete.tsx';

interface Props {
  contactId?: number;
}

const AddContactToProjectModal: React.FC<Props> = ({ contactId: contactIdProp }) => {
  const record = useRecordContext<{ id?: number }>();
  const contactId = contactIdProp ?? record?.id;
  const [open, setOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    null,
  );
  const [stage, setStage] = useState<ProjectContactStage>(
    ProjectContactStage.INITIAL,
  );
  const [create, { isLoading }] = useCreate();

  const notify = useNotify();
  const refresh = useRefresh();

  const reset = () => {
    setSelectedProjectId(null);
    setStage(ProjectContactStage.INITIAL);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const handleAdd = () => {
    if (!contactId) {
      notify('Contact not loaded yet', { type: 'warning' });
      return;
    }
    if (!selectedProjectId) {
      notify('Select a project to add the contact to', { type: 'warning' });
      return;
    }

    create(
      'project-contacts',
      {
        data: {
          contactId,
          projectId: selectedProjectId,
          stage,
        },
      },
      {
        onSuccess: () => {
          notify('Contact added to project');
          refresh();
          handleClose();
        },
        onError: (error) => {
          if (error instanceof Error) {
            notify(`Error: ${error.message}`, { type: 'error' });
          } else {
            notify('An unexpected error occurred', { type: 'error' });
          }
        },
      },
    );
  };

  return (
    <>
      <Button variant="contained" color="primary" size="small" onClick={() => setOpen(true)}>
        Add to Project
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Contact to Project</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <ProjectAutocomplete
              value={selectedProjectId}
              onChange={(id) => setSelectedProjectId(id)}
              label="Select Project"
            />
            <FormControl fullWidth>
              <InputLabel id="add-to-project-stage-label">Stage</InputLabel>
              <Select
                labelId="add-to-project-stage-label"
                value={stage}
                label="Stage"
                onChange={(e) =>
                  setStage(e.target.value as ProjectContactStage)
                }
              >
                {(Object.keys(ProjectContactStage) as ProjectContactStage[]).map(
                  (s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleAdd} variant="contained" disabled={isLoading}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddContactToProjectModal;
